/**
 * Edit Agent — Mode selector + manual editor
 *
 * Two modes:
 * 1. Edit with AI → /agents/builder (pre-loaded with agent data)
 * 2. Edit Manually → inline form editor
 *
 * Same pattern as Create Agent (new.tsx) but pre-populated with existing data.
 */

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState, useEffect } from 'react';
import {
  ArrowLeft, Sparkles, Settings2, Save, Bot,
  ChevronRight, Wrench, BookOpen, Plus, X, Search,
  Check,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { Separator } from '@/components/ui/separator';
import { MarkdownRenderer } from '@/components/chat/MarkdownRenderer';
import { IconPicker, AgentIcon } from '@/components/shared/icon-picker';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import {
  AGENT_CATEGORY_LABELS,
  type AgentCategory,
  type AgentAutonomy,
  type GuardrailProfile,
  type AgentVisibility,
} from '@/lib/mock-agents-data';
import { useAgent, useUpdateAgent } from '@/lib/use-agents';
import { useWorkspaceTools } from '@/lib/use-workspace-tools';
// Pod-BBB #750 AGENTS-EDIT-023 / AGENTS-MOCK-005 — replace the local
// MOCK_SKILLS array with the governed Studio catalog so manual edit
// stays in lockstep with `/studio/skills`.
import { useStudioSkills } from '@/lib/use-studio-skills';

export const Route = createFileRoute('/agents/$agentId/edit')({
  component: EditAgentPage,
});

type EditMode = 'select' | 'manual';

function EditAgentPage() {
  const { agentId } = Route.useParams();
  const navigate = useNavigate();
  // Real-data: custom_agents detail
  const { data: original, isLoading } = useAgent(agentId);
  const updateAgent = useUpdateAgent();

  const [mode, setMode] = useState<EditMode>('select');
  // Track whether form state has been hydrated from the (async) load.
  // Without this guard the initial render runs with original=undefined
  // and every field is empty, which is what bug #99 reported.
  const [hydrated, setHydrated] = useState(false);

  // Manual editor state — populated from agent once it loads.
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [icon, setIcon] = useState('bot');
  const [iconColor, setIconColor] = useState('blue');
  const [category, setCategory] = useState<AgentCategory>('custom');
  const [visibility, setVisibility] = useState<AgentVisibility>('team');
  const [systemPrompt, setSystemPrompt] = useState('');
  const [previewPrompt, setPreviewPrompt] = useState(false);
  const [autonomy, setAutonomy] = useState<AgentAutonomy>('assisted');
  const [guardrails, setGuardrails] = useState<GuardrailProfile>('standard');
  const [maxSteps, setMaxSteps] = useState('10');
  const [modelId, setModelId] = useState('argus-default');
  const [selectedTools, setSelectedTools] = useState<Set<string>>(new Set());
  const [selectedSkills, setSelectedSkills] = useState<Set<string>>(new Set());
  const [toolSearch, setToolSearch] = useState('');
  const [triggerChat, setTriggerChat] = useState(true);
  const [triggerScheduled, setTriggerScheduled] = useState(false);
  const [triggerEvent, setTriggerEvent] = useState(false);

  // Hydrate form state when the agent record arrives. Only runs once
  // per agent so user edits aren't clobbered on cache refetch.
  useEffect(() => {
    if (!original || hydrated) return;
    setName(original.name ?? '');
    setDescription(original.description ?? '');
    setIcon(original.icon ?? 'bot');
    setIconColor(original.iconColor ?? 'blue');
    setCategory(original.category ?? 'custom');
    setVisibility(original.visibility ?? 'team');
    setSystemPrompt(original.systemPrompt ?? '');
    setAutonomy(original.autonomyLevel ?? 'assisted');
    setGuardrails(original.guardrailProfile ?? 'standard');
    setMaxSteps(String(original.maxSteps ?? 10));
    setModelId(original.modelId ?? 'argus-default');
    setSelectedTools(new Set(original.tools ?? []));
    setSelectedSkills(new Set(original.skills ?? []));
    setTriggerChat(original.triggerModes.includes('conversational'));
    setTriggerScheduled(original.triggerModes.includes('scheduled'));
    setTriggerEvent(original.triggerModes.includes('event'));
    setHydrated(true);
  }, [original, hydrated]);

  // Section collapse state
  const [sectionsOpen, setSectionsOpen] = useState<Record<string, boolean>>({
    identity: true, prompt: true, model: false, tools: false,
    skills: false, guardrails: false, triggers: false,
  });

  const toggleSection = (key: string) => {
    setSectionsOpen(prev => ({ ...prev, [key]: !prev[key] }));
  };

  // Studio tools for the picker — sourced from /workspace/tools (real
  // catalog), matching what /studio/tools renders. Pre-fix this called
  // useStudioTools() backed by mock data, so Agents could attach tools
  // that don't exist in the governed Studio catalog (Codex F-S03 / #3).
  const { data: workspaceToolsResp } = useWorkspaceTools();
  const allTools = workspaceToolsResp?.data ?? [];
  const filteredTools = allTools.filter(t =>
    !toolSearch || t.name.toLowerCase().includes(toolSearch.toLowerCase()) || t.displayName.toLowerCase().includes(toolSearch.toLowerCase())
  );

  // Pod-BBB #750 — pull real tenant + system skills from the governed
  // Studio catalog (the same source `/studio/skills` renders). The
  // pre-fix `MOCK_SKILLS` array hardcoded 8 names that may or may not
  // exist for the current tenant, letting agents persist references
  // that resolve to nothing at runtime. `scope: 'all'` mirrors the
  // Skills page so cloneable system skills are visible too.
  const { data: studioSkillsResp } = useStudioSkills({ activeOnly: true, scope: 'all' });

  if (isLoading || (!original && !hydrated)) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center p-6">
        <Bot className="h-12 w-12 text-muted-foreground/30 mb-3 animate-pulse" />
        <p className="text-sm text-muted-foreground">Loading agent…</p>
      </div>
    );
  }

  if (!original) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center p-6">
        <Bot className="h-12 w-12 text-muted-foreground/30 mb-3" />
        <h2 className="text-lg font-semibold">Agent Not Found</h2>
        <Button variant="outline" className="mt-4" onClick={() => navigate({ to: '/agents' })}>
          <ArrowLeft className="h-4 w-4 mr-2" /> Back to Agents
        </Button>
      </div>
    );
  }

  const isBuiltin = original.isBuiltin;

  const isDirty = name !== original.name || description !== original.description
    || systemPrompt !== original.systemPrompt || category !== original.category
    || autonomy !== original.autonomyLevel || guardrails !== original.guardrailProfile
    || Number(maxSteps) !== original.maxSteps || icon !== original.icon
    || iconColor !== original.iconColor;

  const handleSave = () => {
    if (isBuiltin) {
      toast.error('Built-in agents cannot be edited — clone first to customize');
      return;
    }
    if (!name.trim() || !systemPrompt.trim()) {
      toast.error('Name and System Prompt are required');
      return;
    }
    // fix-wave / agents-approvals-cluster #1109 — systemPrompt is now
    // persisted through PATCH (backend Zod body accepts it; the SET
    // clause spreads it into the update). autonomy, tools, guardrails
    // are still pending wiring — the toast is honest about which fields
    // round-tripped vs which the user still has to manage elsewhere.
    const trimmedPrompt = systemPrompt.trim();
    updateAgent.mutate(
      {
        id: agentId,
        body: {
          name: name.trim(),
          description: description.trim() || undefined,
          category,
          // Only send the prompt when the user changed it. Sending the
          // original value would still succeed, but we keep the wire
          // small and the audit log signal precise.
          ...(trimmedPrompt && trimmedPrompt !== (original?.systemPrompt ?? '').trim()
            ? { systemPrompt: trimmedPrompt }
            : {}),
          // Platform audit 2026-06-12: autonomyLevel now round-trips through PATCH
          // (backend Zod accepts it + the custom_agents view trigger persists it —
          // proven on ai1). Guardrails has NO backend column yet, so it stays
          // UI-only; the toast is honest rather than pretending it saved.
          autonomyLevel: autonomy,
        },
      },
      {
        onSuccess: () => {
          toast.success(`${name} saved`, {
            description:
              'Name, description, category, system prompt, and autonomy level were saved. Guardrails are not yet persisted (no backend field); tools are managed on their own tab.',
          });
          navigate({ to: '/agents/$agentId', params: { agentId } });
        },
        onError: (e) => toast.error('Save failed', { description: e.message }),
      },
    );
  };

  const toggleTool = (toolName: string) => {
    setSelectedTools(prev => {
      const next = new Set(prev);
      if (next.has(toolName)) next.delete(toolName);
      else next.add(toolName);
      return next;
    });
  };

  const toggleSkill = (skill: string) => {
    setSelectedSkills(prev => {
      const next = new Set(prev);
      if (next.has(skill)) next.delete(skill);
      else next.add(skill);
      return next;
    });
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="border-b border-border/40 bg-background px-6 py-3 flex items-center gap-3 shrink-0">
        <Button variant="ghost" size="icon" className="h-8 w-8" title="Back to Agent" onClick={() => {
          if (mode === 'manual' && isDirty && !confirm('You have unsaved changes. Discard?')) return;
          navigate({ to: '/agents/$agentId', params: { agentId } });
        }}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <Separator orientation="vertical" className="h-5" />
        <AgentIcon iconName={original.icon} colorName={original.iconColor} size="md" />
        <div className="flex-1 min-w-0">
          <h1 className="text-lg font-semibold">Edit: {original.name}</h1>
          <p className="text-xs text-muted-foreground">{original.isBuiltin ? 'Builtin agent — some fields may be read-only' : 'Custom agent — all fields editable'}</p>
        </div>
        {mode === 'manual' && (
          <div className="flex items-center gap-2">
            {isDirty && !isBuiltin && <Badge variant="outline" className="text-[9px] bg-amber-500/10 text-amber-600 dark:text-amber-400">Unsaved</Badge>}
            <Button variant="outline" size="sm" onClick={() => {
              if (isDirty && !isBuiltin && !confirm('You have unsaved changes. Discard?')) return;
              navigate({ to: '/agents/$agentId', params: { agentId } });
            }}>Cancel</Button>
            <Button
              size="sm"
              className="gap-1.5"
              onClick={handleSave}
              disabled={isBuiltin || !isDirty || updateAgent.isPending}
              title={isBuiltin ? 'Built-in agents cannot be edited — clone first' : undefined}
            >
              <Save className="h-3.5 w-3.5" />
              {updateAgent.isPending ? 'Saving…' : 'Save Changes'}
            </Button>
          </div>
        )}
      </div>

      <div className="flex-1 overflow-auto p-6">
        {/* Mode selector */}
        {mode === 'select' && (
          <div className="max-w-2xl mx-auto space-y-4">
            {/* Edit with AI */}
            <Card className="cursor-pointer hover:border-primary/30 transition-colors group" onClick={() => navigate({ to: '/agents/builder', search: { model: undefined, editAgentId: agentId } })}>
              <CardContent className="pt-5 pb-4">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                    <Sparkles className="h-6 w-6 text-primary" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className="text-base font-semibold">Edit with AI</h3>
                      <Badge className="text-[9px]">Recommended</Badge>
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">Describe the changes you want and Argus Architect will update the agent configuration. The builder will be pre-loaded with the current settings.</p>
                  </div>
                  <ChevronRight className="h-5 w-5 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity mt-1" />
                </div>
              </CardContent>
            </Card>

            {/* Edit Manually */}
            <Card className="cursor-pointer hover:border-primary/30 transition-colors group" onClick={() => setMode('manual')}>
              <CardContent className="pt-5 pb-4">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-muted flex items-center justify-center shrink-0">
                    <Settings2 className="h-6 w-6 text-muted-foreground" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className="text-base font-semibold">Edit Manually</h3>
                      <Badge variant="secondary" className="text-[9px]">Full control</Badge>
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">Directly edit identity, system prompt, model, tools, skills, guardrails, and triggers. All fields are pre-populated with the current configuration.</p>
                  </div>
                  <ChevronRight className="h-5 w-5 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity mt-1" />
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Manual editor — collapsible sections */}
        {mode === 'manual' && (
          <div className="flex gap-6 min-h-0">
            {/* Left: Form sections */}
            <div className="flex-1 max-w-2xl space-y-3">
              <Button variant="ghost" size="sm" className="gap-1 -ml-2 mb-2" onClick={() => {
                if (isDirty && !isBuiltin && !confirm('You have unsaved changes. Discard?')) return;
                setMode('select');
              }}>
                <ArrowLeft className="h-3.5 w-3.5" /> Back to options
              </Button>

              {isBuiltin && (
                <div className="rounded-md border border-amber-500/30 bg-amber-500/5 p-3 text-xs">
                  <p className="font-medium text-amber-700 dark:text-amber-400">
                    Built-in agent — read-only
                  </p>
                  <p className="text-muted-foreground mt-1">
                    Built-in agents cannot be edited. Clone the agent
                    from its detail page to create an editable copy.
                  </p>
                </div>
              )}

              {/* Section 1: Identity */}
              <Collapsible open={sectionsOpen.identity} onOpenChange={() => toggleSection('identity')}>
                <Card>
                  <CollapsibleTrigger asChild>
                    <CardHeader className="cursor-pointer hover:bg-muted/30 transition-colors pb-2">
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-sm flex items-center gap-1.5">1. Identity</CardTitle>
                        <Badge variant="secondary" className="text-[9px]">{sectionsOpen.identity ? 'Collapse' : 'Expand'}</Badge>
                      </div>
                    </CardHeader>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <CardContent className="space-y-3 pt-0">
                      <div className="flex items-start gap-4">
                        <div>
                          <Label className="text-xs mb-1.5 block">Icon</Label>
                          <IconPicker value={{ icon, color: iconColor }} onChange={v => { setIcon(v.icon); setIconColor(v.color); }} />
                        </div>
                        <div className="flex-1 space-y-3">
                          <div className="space-y-1.5">
                            <Label className="text-xs">Name<span className="text-red-500 ml-0.5">*</span></Label>
                            <Input value={name} onChange={e => setName(e.target.value)} className="h-8 text-xs" maxLength={60} />
                          </div>
                          <div className="space-y-1.5">
                            <Label className="text-xs">Description</Label>
                            <Textarea value={description} onChange={e => setDescription(e.target.value)} rows={2} className="text-xs" maxLength={300} />
                          </div>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1.5">
                          <Label className="text-xs">Category</Label>
                          <Select value={category} onValueChange={v => setCategory(v as AgentCategory)}>
                            <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {Object.entries(AGENT_CATEGORY_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-1.5">
                          <Label className="text-xs">Visibility</Label>
                          <Select value={visibility} onValueChange={v => setVisibility(v as AgentVisibility)}>
                            <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="private">Private</SelectItem>
                              <SelectItem value="team">Team</SelectItem>
                              <SelectItem value="organization">Organization</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    </CardContent>
                  </CollapsibleContent>
                </Card>
              </Collapsible>

              {/* Section 2: System Prompt */}
              <Collapsible open={sectionsOpen.prompt} onOpenChange={() => toggleSection('prompt')}>
                <Card>
                  <CollapsibleTrigger asChild>
                    <CardHeader className="cursor-pointer hover:bg-muted/30 transition-colors pb-2">
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-sm flex items-center gap-1.5">2. System Prompt</CardTitle>
                        <Badge variant="secondary" className="text-[9px]">{sectionsOpen.prompt ? 'Collapse' : 'Expand'}</Badge>
                      </div>
                    </CardHeader>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <CardContent className="pt-0">
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-[10px] text-muted-foreground">{systemPrompt.split(/\s+/).filter(Boolean).length} words</p>
                        <Button variant={previewPrompt ? 'secondary' : 'ghost'} size="sm" className="h-6 text-[10px]" onClick={() => setPreviewPrompt(!previewPrompt)}>
                          {previewPrompt ? 'Edit' : 'Preview'}
                        </Button>
                      </div>
                      {previewPrompt ? (
                        <div className="bg-muted/30 rounded-lg p-4 prose prose-sm dark:prose-invert max-w-none min-h-[200px]">
                          <MarkdownRenderer content={systemPrompt} />
                        </div>
                      ) : (
                        <Textarea value={systemPrompt} onChange={e => setSystemPrompt(e.target.value)} rows={12} className="font-mono text-xs" placeholder="Define the agent's behavior, rules, and capabilities..." />
                      )}
                    </CardContent>
                  </CollapsibleContent>
                </Card>
              </Collapsible>

              {/* Section 3: Model & Behavior */}
              <Collapsible open={sectionsOpen.model} onOpenChange={() => toggleSection('model')}>
                <Card>
                  <CollapsibleTrigger asChild>
                    <CardHeader className="cursor-pointer hover:bg-muted/30 transition-colors pb-2">
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-sm">3. Model & Behavior</CardTitle>
                        <Badge variant="secondary" className="text-[9px]">{sectionsOpen.model ? 'Collapse' : 'Expand'}</Badge>
                      </div>
                    </CardHeader>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <CardContent className="space-y-3 pt-0">
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1.5">
                          <Label className="text-xs">Model</Label>
                          <Select value={modelId} onValueChange={setModelId}>
                            <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="argus-default">Argus Default (72B)</SelectItem>
                              <SelectItem value="argus-fast">Argus Fast (3B)</SelectItem>
                              <SelectItem value="argus-code">Argus Code (32B)</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-1.5">
                          <Label className="text-xs">Max Steps</Label>
                          <Input type="number" value={maxSteps} onChange={e => { const v = e.target.value; if (v === '') { setMaxSteps(''); return; } const n = Number(v); if (!Number.isNaN(n)) setMaxSteps(String(Math.max(1, Math.min(50, Math.trunc(n))))); }} className="h-8 text-xs" min={1} max={50} />
                        </div>
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-xs">Autonomy Level</Label>
                        <RadioGroup value={autonomy} onValueChange={v => setAutonomy(v as AgentAutonomy)} className="grid grid-cols-2 gap-2">
                          {[
                            { value: 'read_only', label: 'Read Only', desc: 'Can only retrieve data' },
                            { value: 'assisted', label: 'Assisted', desc: 'Suggests actions, human confirms' },
                            { value: 'approval_required', label: 'Approval Required', desc: 'Executes after approval' },
                            { value: 'auto_execute', label: 'Auto Execute', desc: 'Runs autonomously' },
                          ].map(opt => (
                            <Label key={opt.value} className={cn('flex items-start gap-2 p-2.5 rounded-lg border cursor-pointer transition-colors', autonomy === opt.value ? 'border-primary bg-primary/5' : 'hover:border-primary/30')}>
                              <RadioGroupItem value={opt.value} className="mt-0.5" />
                              <div>
                                <p className="text-xs font-medium">{opt.label}</p>
                                <p className="text-[10px] text-muted-foreground">{opt.desc}</p>
                              </div>
                            </Label>
                          ))}
                        </RadioGroup>
                      </div>
                    </CardContent>
                  </CollapsibleContent>
                </Card>
              </Collapsible>

              {/* Section 4: Tools */}
              <Collapsible open={sectionsOpen.tools} onOpenChange={() => toggleSection('tools')}>
                <Card>
                  <CollapsibleTrigger asChild>
                    <CardHeader className="cursor-pointer hover:bg-muted/30 transition-colors pb-2">
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-sm flex items-center gap-1.5"><Wrench className="h-3.5 w-3.5" /> 4. Tools ({selectedTools.size})</CardTitle>
                        <Badge variant="secondary" className="text-[9px]">{sectionsOpen.tools ? 'Collapse' : 'Expand'}</Badge>
                      </div>
                    </CardHeader>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <CardContent className="pt-0">
                      <div className="relative mb-2">
                        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                        <Input value={toolSearch} onChange={e => setToolSearch(e.target.value)} placeholder="Search tools..." className="h-7 text-xs pl-8" />
                      </div>
                      <div className="max-h-[250px] overflow-auto space-y-1">
                        {filteredTools.map(tool => (
                          <label key={tool.name} className={cn('flex items-center gap-2 p-2 rounded-lg cursor-pointer transition-colors text-xs', selectedTools.has(tool.name) ? 'bg-primary/5 border border-primary/20' : 'hover:bg-muted/30')}>
                            <Checkbox checked={selectedTools.has(tool.name)} onCheckedChange={() => toggleTool(tool.name)} />
                            <span className="font-mono flex-1 truncate">{tool.name}</span>
                          </label>
                        ))}
                        {filteredTools.length === 0 && (
                          <p className="text-xs text-muted-foreground text-center py-4">No tools matching "{toolSearch}"</p>
                        )}
                      </div>
                    </CardContent>
                  </CollapsibleContent>
                </Card>
              </Collapsible>

              {/* Section 5: Skills */}
              <Collapsible open={sectionsOpen.skills} onOpenChange={() => toggleSection('skills')}>
                <Card>
                  <CollapsibleTrigger asChild>
                    <CardHeader className="cursor-pointer hover:bg-muted/30 transition-colors pb-2">
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-sm flex items-center gap-1.5"><BookOpen className="h-3.5 w-3.5" /> 5. Skills ({selectedSkills.size})</CardTitle>
                        <Badge variant="secondary" className="text-[9px]">{sectionsOpen.skills ? 'Collapse' : 'Expand'}</Badge>
                      </div>
                    </CardHeader>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <CardContent className="pt-0 space-y-1">
                      {/* Pod-BBB #750 — render the governed Studio
                          catalog. `studioSkillsResp?.items` is the
                          same shape `/studio/skills` reads; each row
                          carries scope='tenant'|'system' so the picker
                          shows whether the skill is tenant-local or
                          a system-shipped row a tenant can clone. */}
                      {(studioSkillsResp?.items ?? []).length === 0 ? (
                        <p className="text-[10px] text-muted-foreground py-2">
                          No skills available in the Studio catalog for this tenant yet.
                        </p>
                      ) : (
                        (studioSkillsResp?.items ?? []).map(skill => (
                          <label key={`${skill.scope}:${skill.id}`} className={cn('flex items-center gap-2 p-2 rounded-lg cursor-pointer transition-colors text-xs', selectedSkills.has(skill.name) ? 'bg-primary/5 border border-primary/20' : 'hover:bg-muted/30')}>
                            <Checkbox checked={selectedSkills.has(skill.name)} onCheckedChange={() => toggleSkill(skill.name)} />
                            <span className="font-mono flex-1">{skill.name}</span>
                            <Badge variant="outline" className="text-[9px]">{skill.scope === 'system' ? 'System' : 'Tenant'}</Badge>
                          </label>
                        ))
                      )}
                    </CardContent>
                  </CollapsibleContent>
                </Card>
              </Collapsible>

              {/* Section 6: Guardrails */}
              <Collapsible open={sectionsOpen.guardrails} onOpenChange={() => toggleSection('guardrails')}>
                <Card>
                  <CollapsibleTrigger asChild>
                    <CardHeader className="cursor-pointer hover:bg-muted/30 transition-colors pb-2">
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-sm">6. Guardrails</CardTitle>
                        <Badge variant="secondary" className="text-[9px]">{sectionsOpen.guardrails ? 'Collapse' : 'Expand'}</Badge>
                      </div>
                    </CardHeader>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <CardContent className="pt-0">
                      <div className="space-y-1.5">
                        <Label className="text-xs">Guardrail Profile</Label>
                        <RadioGroup value={guardrails} onValueChange={v => setGuardrails(v as GuardrailProfile)} className="space-y-2">
                          {[
                            { value: 'basic', label: 'Basic', desc: 'Regex pattern scan only (<40ms)' },
                            { value: 'standard', label: 'Standard', desc: 'Regex + LLM classifier + topic drift (<300ms)' },
                            { value: 'maximum', label: 'Maximum', desc: 'Standard + second-model review (<550ms)' },
                          ].map(opt => (
                            <Label key={opt.value} className={cn('flex items-start gap-2 p-2.5 rounded-lg border cursor-pointer transition-colors', guardrails === opt.value ? 'border-primary bg-primary/5' : 'hover:border-primary/30')}>
                              <RadioGroupItem value={opt.value} className="mt-0.5" />
                              <div>
                                <p className="text-xs font-medium">{opt.label}</p>
                                <p className="text-[10px] text-muted-foreground">{opt.desc}</p>
                              </div>
                            </Label>
                          ))}
                        </RadioGroup>
                      </div>
                    </CardContent>
                  </CollapsibleContent>
                </Card>
              </Collapsible>

              {/* Section 7: Triggers */}
              <Collapsible open={sectionsOpen.triggers} onOpenChange={() => toggleSection('triggers')}>
                <Card>
                  <CollapsibleTrigger asChild>
                    <CardHeader className="cursor-pointer hover:bg-muted/30 transition-colors pb-2">
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-sm">7. Triggers</CardTitle>
                        <Badge variant="secondary" className="text-[9px]">{sectionsOpen.triggers ? 'Collapse' : 'Expand'}</Badge>
                      </div>
                    </CardHeader>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <CardContent className="space-y-3 pt-0">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-xs font-medium">Conversational</p>
                          <p className="text-[10px] text-muted-foreground">User can invoke via Ask Argus</p>
                        </div>
                        <Switch checked={triggerChat} onCheckedChange={setTriggerChat} />
                      </div>
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-xs font-medium">Scheduled</p>
                          <p className="text-[10px] text-muted-foreground">Run on a cron schedule</p>
                        </div>
                        <Switch checked={triggerScheduled} onCheckedChange={setTriggerScheduled} />
                      </div>
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-xs font-medium">Event-triggered</p>
                          <p className="text-[10px] text-muted-foreground">React to alarms, thresholds, etc.</p>
                        </div>
                        <Switch checked={triggerEvent} onCheckedChange={setTriggerEvent} />
                      </div>
                    </CardContent>
                  </CollapsibleContent>
                </Card>
              </Collapsible>

              {/* Save bar */}
              <div className="flex items-center gap-2 pt-4">
                <Button variant="outline" size="sm" onClick={() => {
                  if (isDirty && !isBuiltin && !confirm('Discard changes?')) return;
                  navigate({ to: '/agents/$agentId', params: { agentId } });
                }}>Cancel</Button>
                <Button
                  size="sm"
                  className="gap-1.5"
                  onClick={handleSave}
                  disabled={isBuiltin || !isDirty || updateAgent.isPending}
                  title={isBuiltin ? 'Built-in agents cannot be edited — clone first' : undefined}
                >
                  <Save className="h-3.5 w-3.5" />
                  {updateAgent.isPending ? 'Saving…' : 'Save Changes'}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
